import axios from "axios";
import * as Sentry from "@sentry/react";

/**
 * Safe, stable metadata for one Sentry event.
 *
 * Never put request bodies, auth headers, tokens or full Axios configs in
 * here. Only routing/status metadata and the backend's user-facing text are
 * allowed.
 */
export type ReportErrorContext = {
  /** Coarse area, e.g. "api", "auth", "upload". */
  area?: string;
  /** Function/API operation, e.g. "loginAPI". */
  operation?: string;
  method?: string;
  endpoint?: string;
  httpStatus?: number | string;
  apiStatus?: number | string;
  screen?: string;
  /** Override the default level derived from the backend envelope status. */
  level?: "warning" | "error";
  /** Safe backend text when the thrown value is not an Axios error. */
  message?: string;
};

type ErrorDetails = {
  area: string;
  operation?: string;
  method?: string;
  endpoint?: string;
  httpStatus?: number | string;
  apiStatus?: number | string;
  backendMessage?: string;
};

const EXPECTED_API_STATUSES = new Set([3, 4, 5, 6]);
const MAX_MESSAGE_LENGTH = 500;

const truncate = (value: string) =>
  value.length > MAX_MESSAGE_LENGTH
    ? `${value.slice(0, MAX_MESSAGE_LENGTH)}…`
    : value;

/**
 * Keep only the path part of an endpoint: no origin, query string or hash.
 * Query strings can contain emails, ids or one-time tokens, so they must not
 * become Sentry tags.
 */
export const sanitizeEndpoint = (
  raw?: string | null,
): string | undefined => {
  if (typeof raw !== "string" || !raw.trim()) return undefined;

  let path = raw.trim().split(/[?#]/)[0];
  path = path.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]+/i, "");

  if (!path) return "/";
  return path.startsWith("/") ? path : `/${path}`;
};

export const isExpectedApiStatus = (
  status?: number | string,
): boolean => {
  if (status === undefined || status === null || status === "") return false;
  return EXPECTED_API_STATUSES.has(Number(status));
};

const readSafeText = (value: unknown): string | undefined => {
  if (typeof value === "string" && value.trim()) {
    return truncate(value.trim());
  }
  return undefined;
};

/** Read only the backend's display text — never the full response object. */
const getBackendMessage = (data: unknown): string | undefined => {
  const direct = readSafeText(data);
  if (direct) return direct;
  if (!data || typeof data !== "object") return undefined;

  const record = data as Record<string, unknown>;
  for (const key of ["resp", "message", "msg", "error", "detail"]) {
    const text = readSafeText(record[key]);
    if (text) return text;
  }
  return undefined;
};

const describeUnknown = (value: unknown): string => {
  if (typeof value === "string") return readSafeText(value) ?? "Unknown error";
  if (typeof value === "number" || typeof value === "boolean") {
    return `Non-Error exception: ${String(value)}`;
  }
  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    for (const key of ["message", "resp", "msg", "error", "detail"]) {
      const text = readSafeText(record[key]);
      if (text) return text;
    }
    if (typeof record.status !== "undefined") {
      return `Request failed with status ${String(record.status)}`;
    }
  }
  return "Unknown error";
};

const setCause = (error: Error, cause: unknown) => {
  try {
    if ((error as Error & { cause?: unknown }).cause === undefined) {
      (error as Error & { cause?: unknown }).cause = cause;
    }
  } catch {
    // `cause` is best-effort only; never let it break reporting.
  }
};

const extractDetails = (
  error: unknown,
  context: ReportErrorContext,
): ErrorDetails => {
  const details: ErrorDetails = {
    area: context.area ?? "api",
    operation: context.operation,
    method: context.method,
    endpoint: sanitizeEndpoint(context.endpoint),
    httpStatus: context.httpStatus,
    apiStatus: context.apiStatus,
    backendMessage: context.message,
  };

  if (axios.isAxiosError(error)) {
    const config = error.config as any;
    const response = error.response as any;

    details.method = details.method ?? config?.method;
    details.endpoint =
      details.endpoint ?? sanitizeEndpoint(config?.url ?? config?.baseURL);
    details.httpStatus = details.httpStatus ?? response?.status;
    details.apiStatus = details.apiStatus ?? response?.data?.status;
    details.backendMessage =
      details.backendMessage ?? getBackendMessage(response?.data);
  } else if (error && typeof error === "object") {
    const record = error as Record<string, any>;
    details.method = details.method ?? record.method;
    details.endpoint =
      details.endpoint ?? sanitizeEndpoint(record.endpoint ?? record.url);
    details.httpStatus = details.httpStatus ?? record.httpStatus;
    details.apiStatus = details.apiStatus ?? record.apiStatus ?? record.status;
    details.backendMessage =
      details.backendMessage ?? getBackendMessage(record);
  }

  return details;
};

const buildAxiosMessage = (
  error: any,
  details: ErrorDetails,
): string => {
  const method = (details.method || "REQUEST").toUpperCase();
  const endpoint = details.endpoint || "unknown endpoint";
  const parts = [`API ${method} ${endpoint}`];

  if (details.httpStatus !== undefined && details.httpStatus !== null) {
    parts.push(`HTTP ${String(details.httpStatus)}`);
  }
  if (details.apiStatus !== undefined && details.apiStatus !== null) {
    parts.push(`status ${String(details.apiStatus)}`);
  }

  const reason =
    details.backendMessage ||
    readSafeText(error?.message) ||
    "request failed";
  return `${parts.join(" ")}: ${reason}`;
};

const normalizeError = (
  error: unknown,
  details: ErrorDetails,
): Error => {
  if (axios.isAxiosError(error)) {
    const normalized = new Error(buildAxiosMessage(error, details));
    setCause(normalized, error);
    return normalized;
  }

  if (error instanceof Error) {
    return error;
  }

  const normalized = new Error(
    details.backendMessage || details.operation || describeUnknown(error),
  );
  setCause(normalized, error);
  return normalized;
};

const compactObject = (
  values: Record<string, unknown>,
): Record<string, unknown> => {
  const entries = Object.entries(values).filter(
    ([, value]) => value !== undefined && value !== null && value !== "",
  );
  return Object.fromEntries(entries);
};

const getDefaultScreen = (): string | undefined => {
  if (typeof window === "undefined") return undefined;
  return sanitizeEndpoint(window.location.pathname);
};

/**
 * Report exactly one Sentry event, with stable tags/fingerprint, from any
 * thrown value. This function never throws.
 */
export const reportError = (
  error: unknown,
  context: ReportErrorContext = {},
): void => {
  try {
    const details = extractDetails(error, context);
    const normalized = normalizeError(error, details);

    const status = details.apiStatus ?? details.httpStatus;
    const level =
      context.level ??
      (isExpectedApiStatus(details.apiStatus) ? "warning" : "error");
    const screen = context.screen ?? getDefaultScreen();

    Sentry.withScope((scope) => {
      const tags: Record<string, string> = { area: details.area };

      const addTag = (key: string, value: unknown) => {
        if (value !== undefined && value !== null && value !== "") {
          tags[key] = String(value);
        }
      };

      addTag("operation", details.operation);
      addTag("api.method", details.method?.toUpperCase());
      addTag("api.endpoint", details.endpoint);
      addTag("http.status", details.httpStatus);
      addTag("api.status", details.apiStatus);
      addTag("screen", screen);

      scope.setTags(tags);
      scope.setContext(
        "api",
        compactObject({
          area: details.area,
          operation: details.operation,
          method: details.method,
          endpoint: details.endpoint,
          httpStatus: details.httpStatus,
          apiStatus: details.apiStatus,
          backendMessage: details.backendMessage,
        }),
      );

      if (screen) {
        scope.setContext("navigation", { route: screen });
      }

      // Dynamic error messages still group by flow/endpoint/status.
      scope.setFingerprint([
        "api",
        details.operation ?? "unknown",
        details.endpoint ?? "unknown",
        String(status ?? "unknown"),
      ]);
      scope.setLevel(level);

      Sentry.captureException(normalized);
    });
  } catch (reportingError) {
    // Reporting must never break the app flow.
    console.warn(
      "[Sentry/reportError] ✗ failed to report error",
      reportingError instanceof Error ? reportingError.message : reportingError,
    );
  }
};
